/**
 * WGChairDetails Component - Working Group Chair and Vice Chair Display - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Display-only working group interface featuring chair/vice chair profiles,
 * update viewing with history navigation, and member profile modals. Shows
 * working group updates in a read-only format with update history selection.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.currentGroup - Current working group data object
 * @param {string} props.currentGroup.workingGroupName - Name of the working group
 * @returns {JSX.Element} The WGChairDetails component
 * 
 * @example
 * <WGChairDetails currentGroup={{workingGroupName: "Policy Working Group"}} />
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ProfileModal from '../../modals/ProfileModal';
import apiHelper from '../../utils/apiHelper';
import { FILE_NAMES } from '../../utils/constants';
import Parser from 'html-react-parser';
import './WGChairDetails.css';

/**
 * Generates initials from first and last names
 * 
 * @param {string} firstName - First name
 * @param {string} lastName - Last name
 * @returns {string} Uppercase initials (e.g., "JD" for John Doe)
 */
function getInitials(firstName, lastName) {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
}

function WGChairDetails({ currentGroup }) {
  const { id } = useParams();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [periodicUpdate, setPeriodicUpdate] = useState(null);
  const [groupMembers, setGroupMembers] = useState({ chairs: [], viceChairs: [] });
  const [updates, setUpdates] = useState([]);
  const [currentUpdateIndex, setCurrentUpdateIndex] = useState(0);

  const [memberImages, setMemberImages] = useState({});



  // Fetch working group data and updates
  useEffect(() => {
    /**
     * Fetches working group member details and updates from API
     * Filters and sorts updates by date, handles validation for required fields
     */
    const fetchData = async () => {
      try {
        const memberResponse = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.WG_MEMBER_DETAILS);

        if (memberResponse && Array.isArray(memberResponse.fileContent)) {
          const groupName = currentGroup.workingGroupName;
          
          const members = memberResponse.fileContent.filter(member => 
            member.workingGroupName === groupName
          );
          
          const chairMembers = members.filter(m => m.workingGroupPosition === "Chair");
          const viceChairMembers = members.filter(m => m.workingGroupPosition === "Vice Chair");

          setGroupMembers({
            chairs: chairMembers || [],
            viceChairs: viceChairMembers || []
          });
        }

        // Then fetch updates...
        const response = await fetch(`${process.env.REACT_APP_API_URL}graph/getWorkingGroupUpdates/${id}`, {
          headers: {
            'Authorization': `Bearer ${await apiHelper.getAuthToken()}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch updates');
        }

        const fetchedUpdates = await response.json();
        
        // Filter out invalid updates (where required fields are undefined)
        const validUpdates = fetchedUpdates.filter(update => 
          update.updateDate && 
          update.update && 
          update.updatedBy
        );

        if (validUpdates.length > 0) {
          const sortedUpdates = validUpdates.sort((a, b) => 
            new Date(b.updateDate) - new Date(a.updateDate)
          );
          setUpdates(sortedUpdates);
          setPeriodicUpdate(sortedUpdates[0]);
        } else {
          setUpdates([]);
          setPeriodicUpdate(null);
        }
      } catch (error) {
        setUpdates([]);
        setPeriodicUpdate(null);
        console.error('Failed to load updates:', error);
      }
    };

    fetchData();
  }, [currentGroup.workingGroupName, id]);

  // Load all member profile images
  useEffect(() => {
    /**
     * Loads profile images for all chairs and vice chairs
     * Creates blob URLs for image display and manages loading states
     */
    const loadMemberImages = async () => {
      const allMembers = [...groupMembers.chairs, ...groupMembers.viceChairs];
      
      if (allMembers.length === 0) return;

      const imagePromises = allMembers
        .filter(member => member.email)
        .map(async (member) => {
          try {
            // First get the profile to get the photo
            const profile = await apiHelper.getMemberProfileByEmail(member.email);
            if (!profile?.photo) {
              return null;
            }

            // Get the image using the photo filename
            const authToken = await apiHelper.getAuthToken();
            const response = await fetch(`${process.env.REACT_APP_API_URL}graph/getMediaContent/profile-images/${profile.photo}`, {
              headers: {
                'Authorization': `Bearer ${authToken}`
              }
            });

            if (!response.ok) {
              return null;
            }

            const blob = await response.blob();
            const imageUrl = URL.createObjectURL(blob);
            return { email: member.email, url: imageUrl };
          } catch (error) {
            return null;
          }
        });

      const loadedImages = await Promise.all(imagePromises);
      const imageMap = {};
      loadedImages.forEach(img => {
        if (img) {
          imageMap[img.email] = img.url;
        }
      });
      setMemberImages(imageMap);
    };

    loadMemberImages();

    // Cleanup function to revoke blob URLs
    return () => {
      Object.values(memberImages).forEach(url => {
        if (url?.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [groupMembers.chairs, groupMembers.viceChairs]);

  /**
   * Handles profile card click to open member profile modal
   * 
   * @param {Object} member - Member object containing profile information
   * @param {string|number} member.id - Unique member identifier
   */
  const handleProfileClick = (member) => {
    setProfileData({id: member.id });
    setShowProfileModal(true);
  }

  const combinedMembers = [...groupMembers.chairs, ...groupMembers.viceChairs];
  const midpoint = Math.ceil(combinedMembers.length / 2);
  const leftMembers = combinedMembers.slice(0, midpoint);
  const rightMembers = combinedMembers.slice(midpoint);

   return (
    <>
      <div className="wg-chair-container">
        <div className="wg-leadership-row">
          {/* Left Members */}
          {combinedMembers.length > 0 && (
            <div className="wg-members-column">
              {leftMembers.map((member, index) => (
                <div key={`member-left-${member.id || index}`} className="wg-member-card" onClick={() => handleProfileClick(member)}>
                  {memberImages[member.email] ? (
                    <img src={memberImages[member.email]} alt="Profile" className="wg-member-image" />
                  ) : (
                    <div className="wg-member-initials">{getInitials(member.firstName, member.lastName)}</div>
                  )}
                  <h4 className="wg-member-name">{`${member.firstName} ${member.lastName}`}</h4>
                  <p className="wg-member-role">{member.workingGroupPosition}</p>
                </div>
              ))}
            </div>
          )}

          {/* Updates Section - always shown */}
          <div className="wg-updates-section">
            <div className="monthly-update">
              <div className="update-header">
                <h3>
                  Update: {currentGroup.workingGroupName}
                  {periodicUpdate?.updateDate && (
                    <span className="update-date">
                      {" - "}
                      {new Date(periodicUpdate.updateDate).toLocaleString('default', {
                        month: 'long',
                        year: 'numeric'
                      })}
                    </span>
                  )}
                </h3>
                {updates?.length > 1 && periodicUpdate && (
                  <select
                    className="update-selector"
                    value={currentUpdateIndex}
                    onChange={(e) => {
                      const newIndex = parseInt(e.target.value);
                      setCurrentUpdateIndex(newIndex);
                      setPeriodicUpdate(updates[newIndex]);
                    }}
                  >
                    {updates.map((update, index) => (
                      <option key={index} value={index}>
                        {new Date(update.updateDate).toLocaleString('default', {
                          month: 'long',
                          year: 'numeric'
                        })}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="update-display">
                {periodicUpdate?.update ? (
                  <div className="update-content">
                    {Parser(periodicUpdate.update)}
                  </div>
                ) : (
                  <div className="no-updates">
                    <p>No recent updates available for this working group.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Members */}
          {combinedMembers.length > 0 && (
            <div className="wg-members-column">
              {rightMembers.map((member, index) => (
                <div key={`member-right-${member.id || index}`} className="wg-member-card" onClick={() => handleProfileClick(member)}>
                  {memberImages[member.email] ? (
                    <img src={memberImages[member.email]} alt="Profile" className="wg-member-image" />
                  ) : (
                    <div className="wg-member-initials">{getInitials(member.firstName, member.lastName)}</div>
                  )}
                  <h4 className="wg-member-name">{`${member.firstName} ${member.lastName}`}</h4>
                  <p className="wg-member-role">{member.workingGroupPosition}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showProfileModal && (
        <ProfileModal
          show={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          small={true}
          profileData={profileData}
        />
      )}
    </>
  );
}

export default WGChairDetails;
